import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useFeature } from '../../stores/featureStore';
import { useClassroomStore } from '../../features/classroom/store';
import { livePhase } from '../../features/classroom/runSheet/livePhase';

// design/LiveClass.dc.html row 3 — "the class command bar … fixed at the top,
// never scrolled away". That mockup's row also carries step actions (Stop
// step / Go live: next / End class) and per-step numbers (answered count,
// pacing budget). This component deliberately does NOT reproduce that: those
// live in features/classroom/runSheet/RunSheetActionBar.tsx (BP 22 / WIP-018)
// already, driven by useRunSheet's own poll and livePhase()'s primary-action
// rule — duplicating them here would mean two places deciding "what button is
// primary right now" from two different data sources.
//
// What THIS bar answers instead is the actual gap: RunSheetActionBar only
// exists on /teach/sessions/:id, so an instructor who steps away mid-class —
// to check a student's Learner plan, to look something up in the Glossary —
// has no idea class is still live. This is that ambient reminder: read-only,
// app-wide (via AppShell), one link back to the run sheet. It shares
// useClassroomStore's existing 5s poll rather than opening a second one, and
// reads livePhase() (a pure function, already exported for exactly this kind
// of shared read) only to name the current step — never its `primary` field.
export default function LiveClassBar() {
  const user = useAuthStore((s) => s.user);
  const isInstructor = !!(user?.is_instructor || user?.is_admin || user?.is_super_admin);
  const classActivitiesEnabled = useFeature('class_activities');
  const { pathname } = useLocation();

  const session = useClassroomStore((s) => s.session);
  const activity = useClassroomStore((s) => s.activity);
  const startWatching = useClassroomStore((s) => s.startWatching);
  const stopWatching = useClassroomStore((s) => s.stopWatching);

  // Instructors keep the shared poll running for as long as they're in the
  // app (not just while on /class or the run sheet) — that's the point: the
  // "am I live" answer has to be available from anywhere. Ref-counted, so an
  // instructor also viewing /class or the run sheet still shares one interval.
  useEffect(() => {
    if (!isInstructor || !classActivitiesEnabled) return undefined;
    startWatching();
    return () => stopWatching();
  }, [isInstructor, classActivitiesEnabled, startWatching, stopWatching]);

  if (!isInstructor || !session || session.status !== 'live') return null;

  // RunSheetActionBar already covers this exact session on its own page —
  // showing both there would be the "two places, one truth" problem this
  // component exists to avoid on every OTHER page.
  if (pathname === `/teach/sessions/${session.id}`) return null;

  const phase = livePhase(activity);

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex shrink-0 items-center gap-3 bg-ws-accent px-3 py-2 text-ws-on-accent sm:px-4"
    >
      <span className="flex shrink-0 items-center gap-1.5">
        <span className="size-2 animate-pulse rounded-full bg-ws-danger" aria-hidden="true" />
        <span className="text-[11px] font-bold uppercase tracking-wider">Live</span>
      </span>

      <span className="hidden shrink-0 h-5 w-px bg-white/25 sm:block" aria-hidden="true" />

      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-semibold leading-tight">{session.label}</p>
        {/* BP 10 gotcha: --ws-* tokens are plain vars, no <alpha-value> — a
            Tailwind opacity modifier on them is a silent no-op. Full-opacity
            on-accent text it is; still >=4.3:1 in all four themes (Button.tsx). */}
        <p className="truncate text-[11px] leading-tight text-ws-on-accent">
          {phase.step ? phase.step.label : 'Nothing live yet — go to the run sheet to release a step.'}
        </p>
      </div>

      <Link
        to={`/teach/sessions/${session.id}`}
        className="inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-white/30 bg-white/10 px-3 text-[13px] font-medium text-ws-on-accent transition-colors hover:bg-white/20"
      >
        Open run sheet
        <ArrowRight size={13} aria-hidden="true" />
      </Link>
    </div>
  );
}
